import BaseScene from './BaseScene';
import PlayerManager from '../components/PlayerManager';
import NodeManager from '../components/NodeManager';
import PathManager from '../components/PathManager';
import CollisionManager from '../components/CollisionManager';
import ButtonSprite from '../components/ButtonSprite';
import { LevelConfig } from '../components/LevelConfig';
import ComputerStrategyFactory from '../components/ComputerStrategyFactory';

export default class Game extends BaseScene {
    
    private players?: PlayerManager;
    private nodes?: NodeManager;
    private paths?: PathManager;
    private collisions?: CollisionManager;
    private timer?: Phaser.Time.TimerEvent;
    private text?: Phaser.GameObjects.Text;
    private levelId?: number;
    protected gameSpeedFactor = 0;
    protected score = 0;
    protected initialPlayerSupply = 0;
    protected initialPlayerPopulation = 0;

    constructor(config: any) {
        super(config);
    }

    public init(levelConfig: LevelConfig): void {
        super.init(levelConfig);
        this.levelId = levelConfig.id;

        // Init Managers
        this.players = new PlayerManager(this);
        this.nodes = new NodeManager(this, ComputerStrategyFactory.create(levelConfig.computerStrategy));
        this.paths = new PathManager(this);
        this.collisions = new CollisionManager(this);

        // Set Game Speed
        this.gameSpeedFactor = this.getBootloaderScene().AppData.gameSpeedFactor();

        // Events
        this.registry.events.on('nodeCaptured', this.actionsToNodeCaptured, this);
    }
       

    public actionsToNodeCaptured(capturerPlayerId: string, loserPlayerId: string): void {
        // Eliminate computer increase score 
        if(capturerPlayerId === 'Player' && loserPlayerId !== 'Neutral' && !this.nodes?.isPlayerInGame(loserPlayerId)) {
            this.increaseScore(10);
        }
    }  

    public getNodes(): NodeManager {
        if (!this.nodes) {
            throw new Error('Nodes is not defined');
        }
        return this.nodes;
    }

    public getPaths(): PathManager {
        if (!this.paths) {
            throw new Error('Paths is not defined');
        }
        return this.paths;
    }

    public getPlayers(): PlayerManager {
        if (!this.players) {
            throw new Error('Players is not defined');
        }
        return this.players;
    }

    public getScore(): number {
        return this.score;
    }

    public increaseScore(increment: number) {
        this.score = parseFloat((this.score + increment).toFixed(4));
        this.registry.events.emit('scoreUpdated', this.score);
    }

    public decreaseScore(decrement: number) {
        if((this.score - decrement) < 0) {
            this.score = 0;
        }

        this.score = parseFloat((this.score - decrement).toFixed(4));
        this.registry.events.emit('scoreUpdated', this.score);
    }

    public preload(): void {
        this.load.setPath('./assets/');
    }

    public create(): void {
        super.create();

        // Music
        this.getBootloaderScene().soundManager.playGameMusic();  

        // Game UI
        this.scene.launch('GameUI', {
            initialScore: this.score,
            initialPlayerSupply: this.initialPlayerSupply,
            initialPlayerPopulation: this.initialPlayerPopulation
        });
        this.scene.bringToTop('GameUI');

        // Timer
        this.timer = this.time.addEvent({ 
            delay: 1000,
            callback: this.timerEvent,
            callbackScope: this,
            repeat: -1,
            loop: false,
            timeScale: 0.4 * this.gameSpeedFactor
        });        

        // Nodes
        this.nodes?.create();

        // Paths
        this.paths?.create();

        // Collisions
        this.collisions?.create();

        // Time Progress
        this.text = this.add.text(32, 32, '');

        // Bottom Panel
        const bottomPanel = this.add.image(0, 0, 'gui_bottom_panel_light_02').setOrigin(.5);
        const pauseButton = new ButtonSprite(this, 210, 8, 'button_pause_green', 2, () => this.pauseGame()).setScale(0.3);
        const bottomPanelContainer = this.add.container(this.width / 2, this.height - 40).add([bottomPanel, pauseButton]);
        this.forceSpriteBottomPosition(bottomPanelContainer, 0);

        // Debug win/lose
        if (this.getBootloaderScene().globals.debugMode && this.input.keyboard) {
            const wKey = this.input.keyboard.addKey('W');
            wKey.on('down', () => {
                this.winGame();
            });
            const lKey = this.input.keyboard.addKey('L');
            lKey.on('down', () => {
                this.loseGame();
            });
        }   
    }

    pauseGame(): void {
        this.scene.pause('GameLevel');
        this.scene.pause('GameUI');
        this.scene.launch('GamePaused');
        this.scene.bringToTop('GamePaused');
    }

    winGame(): void {
        this.scene.pause('GameLevel');
        this.scene.pause('GameUI');
        this.scene.launch('GameWinned', {levelId: this.levelId, score: this.score});
        this.scene.bringToTop('GameWinned');
    }

    loseGame(): void {
        this.scene.pause('GameLevel');
        this.scene.pause('GameUI'); 
        this.scene.launch('GameLosed', {levelId: this.levelId, score: this.score});
        this.scene.bringToTop('GameLosed');
    }

    update(): void {
        
        // Nodes
        this.nodes?.update();

        // Time Progress
        if (this.getBootloaderScene().globals.debugMode) {
            this.text?.setText('Event.progress: ' + this.timer?.getProgress().toString().substr(0, 4));
        }
        
        // Check Status Game for Win
        if(this.nodes?.lastPlayerIdInGame() == 'Player') {
            this.winGame();
        }

        // Check Status Game for Lose
        if(!this.nodes?.isPlayerInGame('Player')) {
            this.loseGame();
        }
    }

    timerEvent(): void {

        const scoreDecrement = (1.6 / this.gameSpeedFactor);
        this.decreaseScore(scoreDecrement);

        if(this.nodes) {
            this.nodes.advanceTime();
        }
    }

    public cleanUp(): void {
        super.cleanUp();
        this.registry.events.off('nodeCaptured');
    } 
}